// Ported from KoPilot (same RBAC admin screens).
// Client-side list tables — page size selector, global search, sortable
// headers and numbered pagination — reproducing the reference admin's
// TableContainer (cinzan-edu-admin). Opt-in: wrap the table in an
// element carrying data-table-list (see app/views/permissions/index).
// Such tables are skipped by the generic DataTables enhancement.
//
// Rows are filtered on their text content and sorted on each cell's
// data-sort-value (falling back to its text).

const PAGINATION_ICONS = {
  previous: '<i class="mdi mdi-chevron-left"></i>',
  next: '<i class="mdi mdi-chevron-right"></i>'
}

export function initTableLists() {
  document.querySelectorAll("[data-table-list]").forEach((root) => {
    if (root.dataset.tableListWired) return
    root.dataset.tableListWired = "true"
    setupTableList(root)
  })
}

function setupTableList(root) {
  const tbody = root.querySelector("tbody")
  const rows = Array.from(tbody.querySelectorAll("tr[data-row]"))
  const emptyRow = tbody.querySelector("tr[data-empty-row]")
  const pageSizeSelect = root.querySelector("[data-table-list-page-size]")
  const searchInput = root.querySelector("[data-table-list-search]")
  const info = root.querySelector("[data-table-list-info]")
  const pagination = root.querySelector("[data-table-list-pagination]")
  const headers = Array.from(root.querySelectorAll("thead th"))

  const state = { pageSize: Number(pageSizeSelect?.value || 10), pageIndex: 0, query: "", sortColumn: null, sortDir: null }

  const cellValue = (row, column) => {
    const cell = row.cells[column]
    if (!cell) return ""
    return cell.dataset.sortValue ?? cell.textContent.trim()
  }

  const compare = (a, b) => {
    const numA = Number(a)
    const numB = Number(b)
    if (a !== "" && b !== "" && !Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
    return a.localeCompare(b, "fr", { sensitivity: "base" })
  }

  const render = () => {
    let visible = rows.filter((row) => !state.query || row.textContent.toLowerCase().includes(state.query))

    if (state.sortColumn !== null) {
      const direction = state.sortDir === "asc" ? 1 : -1
      visible = [...visible].sort((a, b) => direction * compare(cellValue(a, state.sortColumn), cellValue(b, state.sortColumn)))
    }

    const pageCount = Math.max(1, Math.ceil(visible.length / state.pageSize))
    state.pageIndex = Math.min(state.pageIndex, pageCount - 1)
    const start = state.pageIndex * state.pageSize
    const pageRows = visible.slice(start, start + state.pageSize)

    rows.forEach((row) => { row.hidden = true })
    pageRows.forEach((row) => {
      row.hidden = false
      tbody.appendChild(row) // keeps the sorted order in the DOM
    })
    if (emptyRow) {
      emptyRow.hidden = visible.length > 0
      tbody.appendChild(emptyRow)
    }

    // Wording comes from the view (i18n): data-table-list-info-template="Affichage de %{size} sur %{total} résultats"
    if (info) {
      const template = root.dataset.tableListInfoTemplate || "%{size} / %{total}"
      info.textContent = template.replace("%{size}", state.pageSize).replace("%{total}", rows.length)
    }
    if (pagination) renderPagination(pageCount)
  }

  const renderPagination = (pageCount) => {
    const item = (label, index, { disabled = false, active = false, extraClass = "" } = {}) =>
      `<li class="paginate_button page-item ${extraClass} ${disabled ? "disabled" : ""} ${active ? "active" : ""}">` +
      `<a href="#" class="page-link" data-page="${index}">${label}</a></li>`

    let html = item(PAGINATION_ICONS.previous, state.pageIndex - 1, { disabled: state.pageIndex === 0, extraClass: "previous" })
    for (let i = 0; i < pageCount; i++) html += item(i + 1, i, { active: i === state.pageIndex })
    html += item(PAGINATION_ICONS.next, state.pageIndex + 1, { disabled: state.pageIndex >= pageCount - 1, extraClass: "next" })
    pagination.innerHTML = html
  }

  pagination?.addEventListener("click", (event) => {
    const link = event.target.closest("a[data-page]")
    if (!link) return
    event.preventDefault()
    if (link.closest(".disabled")) return
    state.pageIndex = Number(link.dataset.page)
    render()
  })

  pageSizeSelect?.addEventListener("change", () => {
    state.pageSize = Number(pageSizeSelect.value)
    state.pageIndex = 0
    render()
  })

  // Debounced like the reference admin's search input.
  let searchTimeout
  searchInput?.addEventListener("input", () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      state.query = searchInput.value.trim().toLowerCase()
      state.pageIndex = 0
      render()
    }, 500)
  })

  headers.forEach((th, column) => {
    if (!th.hasAttribute("data-sortable")) return
    th.addEventListener("click", () => {
      state.sortDir = state.sortColumn === column && state.sortDir === "asc" ? "desc" : "asc"
      state.sortColumn = column
      headers.forEach((h) => {
        if (!h.classList.contains("sorting")) return
        h.classList.remove("sorting_asc", "sorting_desc")
      })
      th.classList.add(state.sortDir === "asc" ? "sorting_asc" : "sorting_desc")
      render()
    })
  })

  render()
}

// Rails' per-form CSRF tokens are bound to the form's action: once we
// retarget a form, swap in the page-wide token (valid for any form).
function retarget(form, action) {
  form.action = action
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const input = form.querySelector('input[name="authenticity_token"]')
  if (token && input) input.value = token
}

// Create/edit form in a modal (one form for both, like the reference
// admin): buttons carrying data-form-modal-open fill the fields from
// their data-values JSON and point the form at data-action/data-method.
export function initFormModals(Modal) {
  document.querySelectorAll("[data-form-modal]").forEach((modalEl) => {
    if (modalEl.dataset.formModalWired) return
    modalEl.dataset.formModalWired = "true"

    const modal = Modal.getOrCreateInstance(modalEl)
    const form = modalEl.querySelector("form")
    const title = modalEl.querySelector("[data-form-modal-title]")
    const submit = modalEl.querySelector("[data-form-modal-submit]")
    const methodInput = form.querySelector('input[name="_method"]') || form.appendChild(Object.assign(document.createElement("input"), { type: "hidden", name: "_method" }))

    const clearErrors = () => {
      form.querySelectorAll(".is-invalid").forEach((el) => el.classList.remove("is-invalid"))
      form.querySelectorAll("[data-client-error]").forEach((el) => {
        el.textContent = ""
        el.classList.remove("d-block")
      })
    }

    document.querySelectorAll(`[data-form-modal-open="${modalEl.id}"]`).forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        const values = JSON.parse(button.dataset.values || "{}")
        const isEdit = button.dataset.method === "patch"

        clearErrors()
        retarget(form, button.dataset.action)
        methodInput.value = button.dataset.method || "post"
        form.querySelectorAll("[data-field]").forEach((input) => {
          if (input.type === "checkbox") {
            input.checked = values[input.dataset.field] === input.value
            return
          }
          input.value = values[input.dataset.field] ?? ""
          if (input.hasAttribute("data-readonly-on-edit")) input.disabled = isEdit
        })
        // Multi-value fields (checkboxes named "...[]"), e.g. a role's permissions.
        form.querySelectorAll("[data-field-array]").forEach((input) => {
          const selected = (values[input.dataset.fieldArray] || []).map(String)
          input.checked = selected.includes(input.value)
        })
        form.dispatchEvent(new CustomEvent("form-modal:filled"))
        if (title) title.textContent = isEdit ? title.dataset.editLabel : title.dataset.newLabel
        if (submit) submit.textContent = isEdit ? submit.dataset.editLabel : submit.dataset.newLabel
        modal.show()
      })
    })

    // Required-field messages before the round trip (Formik/Yup equivalent).
    form.addEventListener("submit", (event) => {
      let valid = true
      form.querySelectorAll("[data-required-message]").forEach((input) => {
        if (input.disabled) return
        // closest(".mb-3"): Rails wraps a field with errors in div.field_with_errors,
        // so the feedback isn't always the input's sibling (hence d-block too).
        const feedback = input.closest(".mb-3")?.querySelector("[data-client-error]")
        const missing = input.value.trim() === ""
        input.classList.toggle("is-invalid", missing)
        if (feedback) {
          feedback.textContent = missing ? input.dataset.requiredMessage : ""
          feedback.classList.toggle("d-block", missing)
        }
        if (missing) valid = false
      })
      if (!valid) event.preventDefault()
    })

    // Re-rendered after a failed save: reopen with the server's errors.
    if (modalEl.dataset.openOnLoad === "true") modal.show()
  })
}

// Delete confirmation modal: buttons carrying data-delete-modal-open point
// its form at their data-action.
export function initDeleteModals(Modal) {
  document.querySelectorAll("[data-delete-modal]").forEach((modalEl) => {
    if (modalEl.dataset.deleteModalWired) return
    modalEl.dataset.deleteModalWired = "true"

    const modal = Modal.getOrCreateInstance(modalEl)
    const form = modalEl.querySelector("form")

    document.querySelectorAll(`[data-delete-modal-open="${modalEl.id}"]`).forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        retarget(form, button.dataset.action)
        modal.show()
      })
    })
  })
}
